import React from "react"
import {Link, useParams} from "react-router-dom"
import {startCase} from "lodash"
import moment from "moment"
import ReactJson from "react-json-view"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAccessToken, useIsUnitTypeUser} from "../../services/auth"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {hiddenValue, OkDocument} from "../../resources/common"
import {
	getUnifiedApplication,
	UnifiedApplication,
	UnifiedApplicationChild,
	UnifiedApplicationChildType,
} from "../../resources/unifiedApplication"
import TitleBar, {MetaItemProps} from "../../components/TitleBar/TitleBar"
import Card from "../../containers/Card/Card"
import HorizontalField from "../../components/HorizontalField/HorizontalField"
import {InformationHiding, emailPreview} from "../../components/InformationHiding/InformationHiding"
import {OrgName} from "../../components/OrgName/OrgName"
import {StatusBanner} from "../../components/StatusBanner/StatusBanner"
import Tag from "../../components/Tag/Tag"
import {CreatedAtTag, Tags, UnifiedApplicationStatusTag, unifiedApplicationStatusTheme} from "../../components/Tag"
import {getChildLine, getChildOwner, getStatusBanner} from "./unifiedApplicationStatusCopy"

function useCanRevealPii() {
	const isSuper = useIsUnitTypeUser("super")
	const isAdmin = useIsUnitTypeUser("unit-admin")
	const isCompliance = useIsUnitTypeUser("compliance")
	return isSuper || isAdmin || isCompliance
}

function StaticText({value}: {value: string}) {
	return <input type="text" readOnly value={value} className="input is-static" />
}

function getTypeLabel(application: UnifiedApplication) {
	return application.type === "businessUnifiedApplication" ? "Business" : "Sole proprietor"
}

function ApplicationDetailsCard({application, canRevealPii}: {application: UnifiedApplication; canRevealPii: boolean}) {
	const {id, attributes, relationships} = application
	const {contactFullName, contactEmail, customerId} = attributes
	const fullName = contactFullName ? `${contactFullName.first} ${contactFullName.last}` : undefined

	return (
		<Card title="Application details" nested>
			<HorizontalField label="ID">
				<StaticText value={id} />
			</HorizontalField>
			<HorizontalField label="Type">
				<StaticText value={getTypeLabel(application)} />
			</HorizontalField>
			<HorizontalField label="Org">
				<OrgName orgId={relationships.org.data.id} element="div" />
			</HorizontalField>
			<HorizontalField label="Customer ID">
				{customerId ? (
					<Link className="link" to={`/customers/${customerId}`}>
						{customerId}
					</Link>
				) : (
					<StaticText value="—" />
				)}
			</HorizontalField>
			<HorizontalField label="Created at">
				<StaticText value={moment(attributes.createdAt).format("L LT")} />
			</HorizontalField>
			<HorizontalField label="Contact name">
				{fullName ? (
					<InformationHiding
						element={(value) => <StaticText value={value} />}
						getValue={() => fullName}
						placeholder={hiddenValue}
						defaultIsHidden
						isHiddenIconVisible={canRevealPii}
					/>
				) : (
					<StaticText value="—" />
				)}
			</HorizontalField>
			<HorizontalField label="Contact email">
				{contactEmail ? (
					<InformationHiding
						element={(value) => <StaticText value={value} />}
						getValue={() => contactEmail}
						placeholder={emailPreview(contactEmail)}
						defaultIsHidden
						isHiddenIconVisible={canRevealPii}
					/>
				) : (
					<StaticText value="—" />
				)}
			</HorizontalField>
		</Card>
	)
}

function ChildCard({
	title,
	childType,
	childApplications,
	customerId,
}: {
	title: string
	childType: UnifiedApplicationChildType
	childApplications: UnifiedApplicationChild[]
	customerId?: string
}) {
	const child = childApplications.find((c) => c.attributes.type === childType)

	if (!child) {
		return (
			<Card title={title}>
				<p>Not started yet.</p>
			</Card>
		)
	}

	const {attributes} = child
	const isUnit = childType === UnifiedApplicationChildType.Unit

	return (
		<Card title={title}>
			<HorizontalField label="Status">
				<Tag
					size="large"
					variant="outline"
					theme={unifiedApplicationStatusTheme(attributes.status)}
					content={startCase(attributes.status)}
				/>
			</HorizontalField>
			<HorizontalField label="Next move">
				<StaticText value={getChildOwner(child)} />
			</HorizontalField>
			<HorizontalField label="Details">
				<span>{getChildLine(child)}</span>
			</HorizontalField>
			{isUnit ? (
				<HorizontalField label="Unit application ID">
					<Link className="link" to={`/applications/${attributes.sourceSystemId}`}>
						{attributes.sourceSystemId}
					</Link>
				</HorizontalField>
			) : (
				<HorizontalField label="Stripe account ID">
					<span className="is-family-monospace">{attributes.sourceSystemId}</span>
				</HorizontalField>
			)}
			<HorizontalField label="Child ID">
				<StaticText value={child.id} />
			</HorizontalField>
			<HorizontalField label="Created at">
				<StaticText value={moment(attributes.createdAt).format("L LT")} />
			</HorizontalField>
			{isUnit && customerId && (
				<HorizontalField label="Customer">
					<Link className="link" to={`/customers/${customerId}`}>
						{customerId}
					</Link>
				</HorizontalField>
			)}
		</Card>
	)
}

function RawDataCard({application, canRevealPii}: {application: UnifiedApplication; canRevealPii: boolean}) {
	const rawData = canRevealPii
		? application
		: {
				...application,
				attributes: {
					...application.attributes,
					...(application.attributes.contactFullName ? {contactFullName: hiddenValue} : {}),
					...(application.attributes.contactEmail ? {contactEmail: hiddenValue} : {}),
				},
		  }

	return (
		<Card title="Raw data">
			<ReactJson src={rawData} displayDataTypes={false} />
		</Card>
	)
}

export function UnifiedApplicationInternal({value}: {value: OkDocument<UnifiedApplication>}) {
	const application = value.data
	const {id, attributes, relationships} = application
	const listReturnPath = useListReturnPath("/applications?tab=unifiedApplication")
	const canRevealPii = useCanRevealPii()
	const banner = getStatusBanner(application)
	const orgId = relationships.org.data.id
	const customerId = attributes.customerId

	const meta: MetaItemProps[] = [
		{
			text: getTypeLabel(application),
			icon: application.type === "businessUnifiedApplication" ? "toolbox" : "user-geometric-action---users",
		},
		{text: "Org", icon: "bank-account--business-products", path: `/orgs/${orgId}`},
		...(customerId
			? [
					{
						text: "Customer",
						icon: "user-geometric-action-money-fund---users",
						path: `/customers/${customerId}`,
					} as MetaItemProps,
			  ]
			: []),
	]

	return (
		<>
			<TitleBar
				title={`Unified application ${id}`}
				breadcrumbs={[{text: "Unified Applications", path: listReturnPath}]}
				meta={meta}
			/>
			<section className="section">
				<Tags>
					<CreatedAtTag createdAt={new Date(attributes.createdAt)} />
					<UnifiedApplicationStatusTag status={attributes.status} />
				</Tags>
				<StatusBanner tone={banner.tone} headline={banner.headline} lines={banner.lines} />
				<div className="columns mt-4">
					<div className="column is-4 unified-application-details-column">
						<ApplicationDetailsCard key={String(canRevealPii)} application={application} canRevealPii={canRevealPii} />
					</div>
					<div className="column">
						<ChildCard
							title="Unit Application"
							childType={UnifiedApplicationChildType.Unit}
							childApplications={attributes.children}
							customerId={customerId}
						/>
						<ChildCard
							title="Processor Application"
							childType={UnifiedApplicationChildType.Stripe}
							childApplications={attributes.children}
						/>
					</div>
				</div>
				<RawDataCard application={application} canRevealPii={canRevealPii} />
			</section>
		</>
	)
}

export default function UnifiedApplicationPage() {
	const {unifiedApplicationId = ""} = useParams<{unifiedApplicationId: string}>()
	const accessToken = useAccessToken()
	const [refreshToken, refresh] = useRefreshToken()
	const asyncResult = useAsyncResult(
		() => getUnifiedApplication(accessToken, unifiedApplicationId),
		[refreshToken, unifiedApplicationId]
	)

	return (
		<AsyncResultComponent asyncResult={asyncResult} refresh={refresh}>
			{({value}) => <UnifiedApplicationInternal value={value} />}
		</AsyncResultComponent>
	)
}
